import { useState } from 'react';

import { Link } from 'react-router-dom';

import SupportModal from './SupportModal';

/**
 * Application footer -- a thin utility strip.
 *
 * Trust markers on the left, navigation on the right, one line on desktop and wrapping
 * on a phone. It closes the page; it does not restate it. No heading, no company
 * description, no attribution paragraph, no copyright block.
 *
 * ---------------------------------------------------------------------------
 * ONLY VERIFIED METERED MINING FACTS APPEAR HERE.
 *
 * The structure follows the reference Sidney supplied; the CONTENT does not. Nothing
 * from that reference site is reproduced -- no founding year, no countries-served count,
 * no third-party statistic. Each marker below is either something this application does
 * (TLS, non-custodial) or a figure already approved for the credibility band.
 *
 * "Non-custodial" is the strongest of them and is literally true: mined bitcoin is paid
 * by the Bitcoin network directly to the customer's own wallet and never passes through
 * Metered Mining (docs/HANDOVER.md section 5).
 *
 * ---------------------------------------------------------------------------
 * NAVIGATION IS NOW REAL.
 *
 * Terms and Privacy were muted labels while Sidney's documents had not been supplied --
 * a link that 404s or a page we invented would both have been worse than an honest gap.
 * The documents arrived, so they are routes: /terms and /privacy, each its own page,
 * because a legal document needs a URL that can be linked to and bookmarked.
 *
 * Support opens a dialog rather than navigating. It is a short form, it is reachable
 * from the foot of every page, and sending someone to a separate page to type three
 * fields would lose whatever they were looking at when they needed help.
 *
 * Cookie settings is deliberately absent: there is no cookie-settings functionality, and
 * a control that does nothing is worse than no control.
 *
 * The icons are inline SVG at 12px. No icon dependency was added for four glyphs.
 */

interface FooterFact {
  label: string;
  /** Decorative: the label carries the meaning, so the glyph is hidden from AT. */
  icon: React.ReactNode;
}

const strokeProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const FACTS: FooterFact[] = [
  {
    label: 'TLS encrypted',
    icon: (
      <>
        <rect x="3" y="7" width="10" height="7" rx="1.5" {...strokeProps} />
        <path d="M5.5 7V4.75a2.5 2.5 0 0 1 5 0V7" {...strokeProps} />
      </>
    ),
  },
  {
    label: 'Non-custodial',
    icon: (
      <>
        <path d="M8 2 13.5 4.2v3.6c0 3.1-2.2 5.6-5.5 6.4-3.3-.8-5.5-3.3-5.5-6.4V4.2Z" {...strokeProps} />
        <path d="M5.8 8.1 7.3 9.6l3-3.2" {...strokeProps} />
      </>
    ),
  },
  {
    // Shortened from "Operating since 2023" at Bertrand's request. The trust line reads
    // as four short signals, and the longest of them was setting the wrap point on a
    // phone. THIS ALSO SHORTENS THE DESKTOP LABEL -- see the report.
    label: 'Since 2023',
    icon: (
      <>
        <circle cx="8" cy="8" r="5.75" {...strokeProps} />
        <path d="M8 4.6V8l2.3 1.4" {...strokeProps} />
      </>
    ),
  },
];

/** The two legal documents, each on its own page. Support is a dialog, below. */
const LEGAL_LINKS = [
  { label: 'Terms', to: '/terms' },
  { label: 'Privacy', to: '/privacy' },
];

export default function SiteFooter() {
  const [supportOpen, setSupportOpen] = useState(false);

  return (
    <footer className="mm-footer" data-testid="site-footer">
      <div className="mm-footer-inner">
        <ul className="mm-footer-facts" data-testid="footer-facts">
          {FACTS.map((fact) => (
            <li key={fact.label} className="mm-footer-fact">
              <svg
                viewBox="0 0 16 16"
                width="12"
                height="12"
                aria-hidden="true"
                focusable="false"
                className="shrink-0"
              >
                {fact.icon}
              </svg>
              {fact.label}
            </li>
          ))}
        </ul>

        {/* The hairline and full width are mobile-only -- see .mm-footer-legal. On
            desktop this is an ordinary inline group on the same row as the markers. */}
        <nav aria-label="Legal and support" className="mm-footer-legal">
          <ul className="mm-footer-nav" data-testid="footer-nav">
            {LEGAL_LINKS.map((link) => (
              <li key={link.label}>
                <Link
                  to={link.to}
                  data-testid={`footer-${link.label.toLowerCase()}`}
                  className="mm-footer-nav-item"
                >
                  {link.label}
                </Link>
              </li>
            ))}

            <li>
              {/* A button, not a link: it opens a dialog and goes nowhere. Marking it as
                  a link would tell assistive technology to expect navigation. */}
              <button
                type="button"
                onClick={() => setSupportOpen(true)}
                data-testid="footer-support"
                className="mm-footer-nav-item"
              >
                Support
              </button>
            </li>
          </ul>
        </nav>
      </div>

      <SupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
    </footer>
  );
}
